import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface AuthState {
  // Auth state
  user: User | null;
  session: Session | null;
  userId: string | null;
  userEmail: string | null;
  
  // Derived/computed
  isAuthenticated: boolean;
  isInitialized: boolean;
  isSessionReady: boolean; // NEW: Supabase client can make authenticated requests
  
  // Actions
  setSession: (session: Session | null) => void;
  setUser: (user: User | null) => void;
  clearAuth: () => void;
  
  // Helper to sync from Supabase (for initial load)
  syncFromSupabase: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      session: null,
      userId: null,
      userEmail: null,
      isAuthenticated: false,
      isInitialized: false,
      isSessionReady: false,

      // Actions
      setSession: (session) => {
        const user = session?.user ?? null;
        set({
          session,
          user,
          userId: user?.id ?? null,
          userEmail: user?.email ?? null,
          isAuthenticated: !!session,
          isSessionReady: !!session, // Session ready when we have a valid session
        });
      },

      setUser: (user) => {
        set({
          user,
          userId: user?.id ?? null,
          userEmail: user?.email ?? null,
          isAuthenticated: !!user,
        });
      },

      clearAuth: () => {
        console.log('🔐 [AuthStore] Clearing auth');
        set({
          user: null,
          session: null,
          userId: null,
          userEmail: null,
          isAuthenticated: false,
          isSessionReady: false,
        });
        useAuthStore.persist.clearStorage();
      },

      syncFromSupabase: async () => {
        console.log('🔐 [AuthStore] Syncing from Supabase...');
        
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('🔐 [AuthStore] Error getting session:', error);
            set({ 
              session: null, 
              user: null,
              userId: null,
              userEmail: null,
              isAuthenticated: false,
              isInitialized: true, 
              isSessionReady: true // Ready even if no session (unauthenticated is a valid state)
            });
            return;
          }
          
          console.log('🔐 [AuthStore] Session synced:', { hasSession: !!session });
          
          if (session) {
            const user = session.user;
            set({
              session,
              user,
              userId: user?.id ?? null,
              userEmail: user?.email ?? null,
              isAuthenticated: true,
              isInitialized: true,
              isSessionReady: true,
            });
          } else {
            set({
              session: null,
              user: null,
              userId: null,
              userEmail: null,
              isAuthenticated: false,
              isInitialized: true,
              isSessionReady: true,
            });
          }
        } catch (error) {
          console.error('🔐 [AuthStore] Sync error:', error);
          set({ 
            isInitialized: true, 
            isSessionReady: true 
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      // Only persist non-sensitive data
      partialize: (state) => ({
        userId: state.userId,
        userEmail: state.userEmail,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Don't call syncFromSupabase here - it will be handled by App.tsx
        // This prevents race conditions between rehydration and Supabase session restoration
        console.log('🔐 [AuthStore] Rehydrated from localStorage');
      },
    }
  )
);

// Subscribe to Supabase auth changes (single source of truth)
supabase.auth.onAuthStateChange((event, session) => {
  console.log('🔐 [Supabase] Auth state changed:', event, {
    hasSession: !!session,
    userId: session?.user?.id
  });
  
  // Skip USER_UPDATED events to prevent deadlock with updateUser calls
  if (event === 'USER_UPDATED') {
    console.log('🔐 [Supabase] Ignoring USER_UPDATED event to prevent deadlock');
    return;
  }
  
  // Log token expiration for debugging
  if (session?.expires_at) {
    const expiresAt = new Date(session.expires_at * 1000);
    const now = new Date();
    const minutesUntilExpiry = Math.round((expiresAt.getTime() - now.getTime()) / 60000);
    console.log('🔐 [Supabase] Token expires in:', minutesUntilExpiry, 'minutes');
  }
  
  // Use setTimeout(0) to defer state updates and release Supabase internal lock
  // This prevents deadlocks when auth operations trigger state changes
  setTimeout(() => {
    const store = useAuthStore.getState();
    store.setSession(session);
    
    // Also mark as initialized when auth state changes
    if (!store.isInitialized) {
      console.log('🔐 [Supabase] Marking as initialized from auth state change');
      useAuthStore.setState({ isInitialized: true, isSessionReady: true });
    }

    // Track platform on sign-in / token refresh
    if (session?.user?.id && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
      import('@/services/data/userTracking').then(({ updateLastSeenPlatform }) => {
        updateLastSeenPlatform(session.user.id);
      });
    }
  }, 0);
});
